/**
 * (owner, Oct 2) Shooting from a vehicle: "make it so that you can shoot (only one handed weapons) from a car if you roll your
 * windows down, but only at a small angle to both sides (to where you could realistically shoot from an open window). Shooting a
 * one handed weapon should be possible to any way from a bike or a convertible or something where you realistically could".
 *
 * The rules, all from content (no ids here):
 *  - the gun: `Weapon.hands` 1 (absent: a pistol is one-handed, everything else needs both). Melee, thrown and placed weapons
 *    never go off from a seat, whatever `hands` says.
 *  - the vehicle: `Vehicle.open` (absent: a motorbike-class vehicle is open) — a one-handed gun fires any way from it.
 *    A closed one fires only with the windows down (O / the Car app), and only out of the side windows: the aim within
 *    WINDOW_HALF (35°) either side of square to the heading, on the left or the right — the driver out of his own window and,
 *    awkwardly, across the passenger's. Not through the windscreen, not out of the back.
 *  - aircraft, tanks, trains, trailers and drones: never (a mounted weapon is another matter — game.ts fires it first).
 * The rule runs in the shared Game step, so online play (server/world.ts runs the Game) has it too; the HUD draws the same arcs.
 */
import { AIRCRAFT_CLASSES, type Vehicle, type VehicleClass, type Weapon } from '../content/types';

/** half the side window's arc, either side of square to the heading */
export const WINDOW_HALF = (35 * Math.PI) / 180;
/** classes nobody leans out of with a pistol (aircraft are AIRCRAFT_CLASSES) */
const SEALED: ReadonlySet<VehicleClass> = new Set(['tank', 'train', 'trailer']);

/** how many hands the weapon takes: `hands`, else a pistol one and the rest two */
export function handsOf(w: Weapon): 1 | 2 { return w.hands ?? (w.class === 'pistol' ? 1 : 2); }
/** a gun that fires held in one hand (no swing, nothing thrown or set down) */
export function oneHanded(w: Weapon): boolean { return handsOf(w) === 1 && w.class !== 'melee' && w.class !== 'thrown' && w.class !== 'mounted' && !w.charge; }
/** the riders sit in the open: `open`, else a motorbike-class vehicle */
export function openVehicle(v: Vehicle): boolean { return v.open ?? v.class === 'bike'; }

/**
 * What a gun can do from this seat: `any` way (an open vehicle), out of the `sides` (a closed one, windows down), or why not —
 * `windows` (they are up), `hands` (a two-handed gun or no gun at all), `none` (an aircraft, a tank, a train).
 */
export type GunArc = 'any' | 'sides' | 'windows' | 'hands' | 'none';
export function gunArc(v: { def: Vehicle; windows?: boolean }, w: Weapon): GunArc {
  if (AIRCRAFT_CLASSES.has(v.def.class) || v.def.flight || SEALED.has(v.def.class)) return 'none';
  if (!oneHanded(w)) return 'hands';
  if (openVehicle(v.def)) return 'any';
  return v.windows ? 'sides' : 'windows';
}

/** how far off the heading the aim is, folded to 0..π (0 dead ahead, π/2 square out of a side window, π dead behind) */
export function offHeading(heading: number, aim: number): number { return Math.abs(Math.atan2(Math.sin(aim - heading), Math.cos(aim - heading))); }
/** is the aim out of one of the side windows (either side) */
export function inWindowArc(heading: number, aim: number): boolean { return Math.abs(offHeading(heading, aim) - Math.PI / 2) <= WINDOW_HALF + 1e-9; }

/** may this gun fire this way from this vehicle: undefined = yes, else the line to show (the driver can roll the windows down himself);
 *  '' = no, said nothing — from an aircraft the aim stick past its ring steers the nose, and is no request to lean out */
export function whyNot(v: { def: Vehicle; windows?: boolean; angle: number }, w: Weapon, aim: number, driver: boolean): string | undefined {
  const arc = gunArc(v, w);
  if (arc === 'any') return undefined;
  if (arc === 'sides') return inWindowArc(v.angle, aim) ? undefined : 'Can\'t shoot that way from inside — only out of the side windows';
  if (arc === 'windows') return driver ? 'Roll the windows down to shoot — O' : 'The windows are up — the driver rolls them down (O)';
  if (arc === 'hands') return `Only a one-handed gun fires from a vehicle — the ${w.name} takes both hands`;
  return '';
}

/** how far from the vehicle's middle its body ends along the direction `aim` (its footprint as a box): where a shot leaves it */
export function bodyReach(v: { def: Vehicle; angle: number }, aim: number): number {
  const rel = aim - v.angle, along = Math.abs(Math.cos(rel)), across = Math.abs(Math.sin(rel));
  const hw = v.def.size[0] / 2, hl = v.def.size[1] / 2;
  return Math.min(across > 1e-6 ? hw / across : Infinity, along > 1e-6 ? hl / along : Infinity);
}
